import { Card, CardContent, Typography, Box, IconButton, LinearProgress, Chip } from '@mui/material';
import { PlayArrow, Pause, Article, CheckCircle, AccessTime } from '@mui/icons-material';
import { formatRelativeDate } from '@utils/time/dateUtils';
import { formatTime } from '@utils/time/timeUtils';
import { getEpisodeNumber } from '@models/episodes/episodeModel';
import EpisodeThumbnail from './EpisodeThumbnail';
import EpisodeSummaries from './EpisodeSummaries';

export default function EpisodeCard({
  episode,
  isPlaying = false,
  progress,
  summaryText,
  shouldTruncate,
  expanded,
  onTranscriptClick,
  onCardClick,
  onCardKeyDown,
  onToggleSummary,
  onPlay
}) {
  const {
    progressPercent,
    totalDuration,
    isStarted,
    isCompleted
  } = progress;
  const episodeNumber = getEpisodeNumber(episode.id);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        cursor: episode.hasTranscript ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)'
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2
        }
      }}
      onClick={onCardClick}
      onKeyDown={onCardKeyDown}
      role={episode.hasTranscript ? 'link' : undefined}
      tabIndex={episode.hasTranscript ? 0 : undefined}
      aria-label={episode.hasTranscript ? `View transcript for ${episode.title}` : undefined}
    >
      <Box
        sx={{
          position: 'relative',
          paddingTop: '56.25%',
          bgcolor: 'action.hover',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <EpisodeThumbnail
            imageUrl={episode.image}
            alt=""
            size="100%"
            episodeNumber={episodeNumber}
          />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: { xs: 1, sm: 0 },
            transition: 'opacity 0.2s',
            '.MuiCard-root:hover &': {
              opacity: 1
            }
          }}
          onClick={onPlay}
        >
          <IconButton
            size="large"
            aria-label={isPlaying ? `Pause ${episode.title}` : `Play ${episode.title}`}
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              width: 56,
              height: 56,
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'scale(1.1)'
              }
            }}
          >
            {isPlaying ? <Pause sx={{ fontSize: 32 }} /> : <PlayArrow sx={{ fontSize: 32 }} />}
          </IconButton>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 0.5
          }}
        >
          {isCompleted && (
            <Chip
              icon={<CheckCircle />}
              label="Completed"
              size="small"
              sx={{
                bgcolor: 'success.main',
                color: 'white',
                fontWeight: 600,
                height: 24
              }}
            />
          )}
          {isPlaying && (
            <Chip
              label="Playing"
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 600,
                height: 24
              }}
            />
          )}
        </Box>

        {totalDuration > 0 && (
          <Chip
            icon={<AccessTime sx={{ fontSize: 14 }} />}
            label={formatTime(totalDuration)}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              height: 24,
              fontSize: '0.75rem'
            }}
          />
        )}
      </Box>

      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 1.5, sm: 2 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                mb: 0.5,
                color: isPlaying ? 'primary.main' : 'text.primary',
                lineHeight: 1.3
              }}
            >
              {episode.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeDate(episode.pubDate)}
            </Typography>
          </Box>

          {episode.hasTranscript && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onTranscriptClick();
              }}
              aria-label={`View transcript for ${episode.title}`}
              title="View transcript"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              <Article fontSize="small" />
            </IconButton>
          )}
        </Box>

        <EpisodeSummaries
          summary={episode.summary}
          aiSummary={episode.aiSummary}
          summaryText={summaryText}
          shouldTruncate={shouldTruncate}
          expanded={expanded}
          onToggleSummary={onToggleSummary}
          compact
        />

        {isStarted && !isCompleted && (
          <Box sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.disabled" fontWeight={500}>
                {Math.round(progressPercent)}% complete
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 3
                }
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
